import './styles.scss';

import { faExclamationCircle, faEye, faEyeSlash, faSearch } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef, InputHTMLAttributes, ReactNode, Ref, useCallback, useState } from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  placeholder?: string;
  errors?: string;
  footer?: string;
  icon?: React.ReactNode;
  className?: string;
}

const Input = forwardRef((props: InputProps, ref: Ref<HTMLInputElement>) => {
  const { label, errors, type, footer, icon, className, ...otherProps } = props;

  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const hasErrors = !!errors?.length;
  const showErrors = hasErrors && errors !== 'empty';
  const hasFooter = !!footer?.length;

  const inputType = type === 'password' && showPassword ? 'input' : type;

  const handleShowPassword = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, [setShowPassword]);

  const renderIcon = () => {
    if (props.type === 'password') {
      return (
        <button type="button" className="icon" onClick={handleShowPassword}>
          <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
        </button>
      );
    }

    if (props.type === 'search') {
      return <FontAwesomeIcon icon={faSearch} />;
    }

    return null;
  };

  const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);

    if (props?.onBlur) {
      props.onBlur(e);
    }
  };

  const handleOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);

    if (props?.onFocus) {
      props.onFocus(e);
    }
  };

  return (
    <div className={`input-container ${className}`}>
      <label className="input-label">
        {label}
        <div className={`input ${isFocused && 'input-focus'} ${hasErrors && 'input-error'}`}>
          {!!icon && <div className="icon">{icon}</div>}
          <input
            ref={ref}
            type={inputType}
            {...otherProps}
            onFocus={handleOnFocus}
            onBlur={handleOnBlur}
          />
          {renderIcon()}
        </div>
      </label>
      {showErrors && (
        <div className="errors">
          <FontAwesomeIcon icon={faExclamationCircle} />
          {errors}
        </div>
      )}
      {hasFooter && !hasErrors && <div className="footer">{footer}</div>}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
